import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  public readonly endpoint = environment.endpoints;

  constructor(private readonly http: HttpClient) { }

  private createHttpParams(params?: any): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return httpParams;
  }

  private createFilterModalHttpParams(params?: any): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (Array.isArray(params[key])) {
          params[key].forEach((val: any) => {
            httpParams = httpParams.append(key, val);
          });
        } else if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return httpParams;
  }

  private createHttpHeaders(headers?: any): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    if (headers) {
      Object.keys(headers).forEach(key => {
        if (headers[key] !== undefined && headers[key] !== null) {
          httpHeaders = httpHeaders.set(key, headers[key]);
        }
      });
    }
    return httpHeaders;
  }

  get<T>(url: string, params?: any, headers?: any, options?: { isFilterModal?: boolean }): Observable<T> {
    
    const httpParams = options?.isFilterModal
      ? this.createFilterModalHttpParams(params)
      : this.createHttpParams(params);
    const httpHeaders = this.createHttpHeaders(headers);


    return this.http.get<T>(url, {
      params: httpParams,
      headers: httpHeaders
    });
  }

  post<T>(url: string, payload: any, params?: any, headers?: any): Observable<T> {
    return this.http.post<T>(url, payload, {
      params: this.createHttpParams(params),
      headers: this.createHttpHeaders(headers)
    });
  }

  put<T>(url: string, payload: any, params?: any, headers?: any): Observable<T> {
    return this.http.put<T>(url, payload, {
      params: this.createHttpParams(params),
      headers: this.createHttpHeaders(headers)
    });
  }

  patch<T>(url: string, payload: any, params?: any, headers?: any): Observable<T> {
    return this.http.patch<T>(url, payload, {
      params: this.createHttpParams(params),
      headers: this.createHttpHeaders(headers)
    });
  }

  delete<T>(url: string, params?: any, headers?: any): Observable<T> {
    return this.http.delete<T>(url, {
      params: this.createHttpParams(params),
      headers: this.createHttpHeaders(headers)
    });
  }

  upload<T>(url: string, formData: FormData, params?: any, headers?: any): Observable<HttpEvent<T>> {
    const req = new HttpRequest('POST', url, formData, {
      params: this.createHttpParams(params),
      headers: this.createHttpHeaders(headers),
      reportProgress: true
    });
    return this.http.request<T>(req);
  }

  download(url: string, params?: any, headers?: any): Observable<Blob> {
    return this.http.get(url, {
      params: this.createHttpParams(params),
      headers: this.createHttpHeaders(headers),
      responseType: 'blob'
    });
  }

  getDonationsList(page: number, limit: number, startDate?: string, endDate?: string, corporateId?: string, searchTerm?: string) {
    const url = `${environment.apiBaseUrl}${environment.endpoints.admin_list_donations}`
      .replace('${page}', page.toString())
      .replace('${limit}', limit.toString());

    const params: any = {};
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }
    if (corporateId) {
      params.corporate_id = corporateId;
    }
    if (searchTerm?.trim()) {
      params.search = searchTerm.trim();
    }

    return this.http.get(url, { params });
  }

  getTotalDonationsAndFees(startDate?: string, endDate?: string, corporateId?: string) {
    const url = `${environment.apiBaseUrl}${environment.endpoints.admin_total_donations_and_fees}`;
    const params: any = {};

    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }
    if (corporateId) {
      params.corporate_id = corporateId;
    }

    return this.http.get(url, { params });
  }

  getCorporatesList(page: number = 1, limit: number = 100) {
    const url = `${environment.apiBaseUrl}${environment.endpoints.admin_corporates}`;
    const params = {
      page: page.toString(),
      limit: limit.toString()
    };

    return this.http.get(url, { params });
  }

  getCorporateProfile() {
    const url = `${environment.apiBaseUrl}${environment.endpoints.corporate_profile}`;
    return this.http.get(url);
  }

  updateCorporateProfile(profileData: any) {
    const url = `${environment.apiBaseUrl}${environment.endpoints.corporate_profile_edit}`;
    return this.http.put(url, profileData);
  }

  getCsrPriorities() {
    const url = `${environment.apiBaseUrl}${environment.endpoints.get_csr_priorities}`;
    return this.http.get(url);
  }

  getPaymentHistory<T>(params: any): Observable<T> {
    const url = `${environment.apiBaseUrl}/v1/corporate/subscriptions-history`;
    return this.http.get<T>(url, { params });
  }
}
